import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Chip, Button, IconButton, Tooltip,
  TextField, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Checkbox
} from "@mui/material";
import {
  Delete as DeleteIcon, Download as DownloadIcon, Print as PrintIcon,
  FileDownload as ExportIcon, PictureAsPdf as PdfIcon,
  InboxOutlined as EmptyIcon, Search as SearchIcon
} from "@mui/icons-material";
import { fetchData, deleteData } from "../../BaseUrl";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import Toast from "../../DynamicComponent/Toast";
import ConfirmDialog from "../../DynamicComponent/ConfirmDialog";
import { exportToCSV } from "../../utils/tableUtils";

const GRADE_PILLS = ["All", "MunMazhalai", "Mazhalai", "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4", "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"];
const SUBJECTS    = ["All Subjects", "Tamil Reading", "Tamil Writing", "Tamil Grammar", "Tamil Speaking", "Cultural Studies", "History"];
const TERMS       = ["All", "Term 1", "Term 2", "Term 3", "Final"];
const YEARS       = ["All", "2024-2025", "2025-2026", "2026-2027"];

const FALLBACK_ROWS = [
  { id: "1", subject: "Tamil Reading",   grade: "Nilai 1", section: "Sec A", examType: "Mid-Term",  term: "Term 1", date: "2025-03-10", uploadedBy: "Mrs. Kavitha",  fileName: "TamilReading_N1_T1.pdf",    fileUrl: "#", documentUrl: "#", status: "Active" },
  { id: "2", subject: "Tamil Writing",   grade: "Nilai 2", section: "Sec B", examType: "Final",     term: "Term 2", date: "2025-04-15", uploadedBy: "Mr. Rajesh",    fileName: "TamilWriting_N2_T2.pdf",    fileUrl: "#", documentUrl: "#", status: "Active" },
  { id: "3", subject: "Tamil Grammar",   grade: "Nilai 3", section: "Sec A", examType: "Unit Test", term: "Term 1", date: "2025-02-20", uploadedBy: "Ms. Priya",     fileName: "TamilGrammar_N3_T1.pdf",    fileUrl: "#", documentUrl: "#", status: "Active" },
  { id: "4", subject: "Tamil Speaking",  grade: "Mazhalai", section: "Sec C", examType: "Final",   term: "Final",  date: "2025-05-01", uploadedBy: "Mrs. Meena",    fileName: "TamilSpeaking_Maz_Final.pdf", fileUrl: "#", documentUrl: "#", status: "Archived" },
  { id: "5", subject: "Cultural Studies",grade: "Nilai 5", section: "Sec A", examType: "Mid-Term",  term: "Term 3", date: "2025-04-22", uploadedBy: "Mr. Arjun",     fileName: "CulturalStudies_N5_T3.pdf", fileUrl: "#", documentUrl: "#", status: "Active" },
  { id: "6", subject: "History",         grade: "Nilai 8", section: "Sec B", examType: "Final",     term: "Final",  date: "2025-05-10", uploadedBy: "Ms. Lakshmi",   fileName: "History_N8_Final.pdf",       fileUrl: "#", documentUrl: "#", status: "Active" },
];

const EXPORT_COLUMNS = [
  { label: "Subject",    field: "subject" },
  { label: "Grade",      field: "grade" },
  { label: "Section",    field: "section" },
  { label: "Exam Type",  field: "examType" },
  { label: "Term",       field: "term" },
  { label: "Date",       field: "date" },
  { label: "Uploaded By",field: "uploadedBy" },
  { label: "File Name",  field: "fileName" },
  { label: "Status",     field: "status" },
];

const TABLE_COLUMNS = [
  { label: "",            field: "checkboxCell" },
  { label: "Subject",     field: "subject" },
  { label: "Grade",       field: "grade" },
  { label: "Section",     field: "section" },
  { label: "Exam Type",   field: "examType" },
  { label: "Term",        field: "term" },
  { label: "Date",        field: "date" },
  { label: "Uploaded By", field: "uploadedBy" },
  { label: "File",        field: "fileCell" },
  { label: "Status",      field: "statusChip" },
  { label: "Actions",     field: "actions" },
];

const AdminQuestionPaper = () => {
  const [rawData, setRawData]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filters, setFilters]         = useState({ grade: "All", subject: "All Subjects", term: "All", year: "All", search: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [toast, setToast]             = useState({ open: false, severity: "success", message: "" });

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.grade && filters.grade !== "All")             p.append("grade",   filters.grade);
    if (filters.subject && filters.subject !== "All Subjects") p.append("subject", filters.subject);
    if (filters.term && filters.term !== "All")               p.append("term",    filters.term);
    if (filters.year && filters.year !== "All")               p.append("year",    filters.year);
    if (filters.search)                                        p.append("search",  filters.search);
    return p.toString();
  }, [filters]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      const result = await fetchData(`admin/question-paper/list${qs ? "?" + qs : ""}`);
      const records = result?.data?.records || result?.data || [];
      setRawData(Array.isArray(records) && records.length > 0 ? records : FALLBACK_ROWS);
    } catch {
      setRawData(FALLBACK_ROWS);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected   = rawData.length > 0 && rawData.every(r => selectedIds.has(r.id));
  const someSelected  = !allSelected && rawData.some(r => selectedIds.has(r.id));

  const handleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(rawData.map(r => r.id)));
  };

  const handleDeleteOne = async (id) => {
    try {
      await deleteData(`admin/question-paper/${id}`);
      setToast({ open: true, severity: "success", message: "Question paper deleted" });
      setDeleteTarget(null);
      loadData();
    } catch {
      setToast({ open: true, severity: "error", message: "Delete failed" });
      setDeleteTarget(null);
    }
  };

  const handleBulkDelete = async () => {
    try {
      for (const id of selectedIds) {
        await deleteData(`admin/question-paper/${id}`);
      }
      setToast({ open: true, severity: "success", message: `${selectedIds.size} paper(s) deleted` });
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      loadData();
    } catch {
      setToast({ open: true, severity: "error", message: "Bulk delete failed" });
      setBulkDeleteOpen(false);
    }
  };

  const handleDownload = (row) => {
    const url = row.fileUrl || row.documentUrl;
    if (url) window.open(url, "_blank");
    else setToast({ open: true, severity: "warning", message: "File URL not available" });
  };

  const handlePrint = (row) => {
    const url = row.fileUrl || row.documentUrl;
    if (!url) { setToast({ open: true, severity: "warning", message: "File URL not available" }); return; }
    const win = window.open(url, "_blank");
    if (win) { win.onload = () => win.print(); }
  };

  const handleExportCSV = () => exportToCSV(EXPORT_COLUMNS, rawData, "question-papers.csv");

  const renderStatusChip = (status) => {
    const map = {
      Active:   { bg: "#e8f5e9", color: "#2e7d32" },
      Archived: { bg: "#f5f5f5", color: "#757575" },
      Draft:    { bg: "#fff3e0", color: "#e65100" },
    };
    const s = map[status] || map.Active;
    return <Chip label={status || "Active"} size="small" sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600 }} />;
  };

  const rows = rawData.map((r) => ({
    ...r,
    checkboxCell: (
      <Checkbox size="small" checked={selectedIds.has(r.id)} onChange={() => toggleSelect(r.id)} />
    ),
    fileCell: r.fileName ? (
      <Chip
        icon={<PdfIcon sx={{ fontSize: 14 }} />}
        label={r.fileName}
        size="small"
        clickable
        onClick={() => handleDownload(r)}
        sx={{ backgroundColor: "#fce4ec", color: "#c62828", fontWeight: 500, maxWidth: 180 }}
      />
    ) : "-",
    statusChip: renderStatusChip(r.status),
    actions: (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="Download">
          <IconButton size="small" onClick={() => handleDownload(r)}>
            <DownloadIcon fontSize="small" sx={{ color: "#244C77" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Print">
          <IconButton size="small" onClick={() => handlePrint(r)}>
            <PrintIcon fontSize="small" sx={{ color: "#2196f3" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => setDeleteTarget(r)}>
            <DeleteIcon fontSize="small" sx={{ color: "#f44336" }} />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }));

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb + Header */}
      <Typography variant="body2" color="text.secondary" mb={0.5}>Pages / Question Paper Management</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Question Paper Management</Typography>
        <Button
          variant="outlined" startIcon={<ExportIcon />} onClick={handleExportCSV}
          sx={{ textTransform: "none" }}
        >
          Export CSV
        </Button>
      </Box>

      {/* Grade Chip Pills */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {GRADE_PILLS.map((g) => (
          <Chip
            key={g} label={g} clickable
            onClick={() => setFilters(p => ({ ...p, grade: g }))}
            sx={{
              backgroundColor: filters.grade === g ? "#244C77" : "#f5f5f5",
              color: filters.grade === g ? "#fff" : "text.primary",
              fontWeight: filters.grade === g ? 600 : 400,
              "&:hover": { backgroundColor: filters.grade === g ? "#244C77" : "#e0e0e0" },
            }}
          />
        ))}
      </Box>

      {/* Filter Bar */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Subject</InputLabel>
          <Select value={filters.subject} label="Subject"
            onChange={(e) => setFilters(p => ({ ...p, subject: e.target.value }))}>
            {SUBJECTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Term</InputLabel>
          <Select value={filters.term} label="Term"
            onChange={(e) => setFilters(p => ({ ...p, term: e.target.value }))}>
            {TERMS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Academic Year</InputLabel>
          <Select value={filters.year} label="Academic Year"
            onChange={(e) => setFilters(p => ({ ...p, year: e.target.value }))}>
            {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField
          size="small" placeholder="Search..." value={filters.search}
          onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && loadData()}
          InputProps={{ startAdornment: <SearchIcon sx={{ color: "#bbb", fontSize: 18, mr: 0.5 }} /> }}
          sx={{ minWidth: 200 }}
        />

        <Button
          variant="contained" onClick={loadData}
          sx={{ height: 40, background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)", textTransform: "none" }}
        >
          Apply
        </Button>
      </Box>

      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: 2, mb: 2, px: 2, py: 1.5,
          backgroundColor: "#EAF1F8", borderRadius: 2, border: "1px solid #c5b8f5"
        }}>
          <Typography variant="body2" fontWeight={600} color="#244C77">
            {selectedIds.size} selected
          </Typography>
          <Button
            variant="outlined" color="error" size="small"
            startIcon={<DeleteIcon />} onClick={() => setBulkDeleteOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Delete Selected
          </Button>
          <Button
            variant="text" size="small" onClick={() => setSelectedIds(new Set())}
            sx={{ textTransform: "none", color: "#757575" }}
          >
            Clear
          </Button>
        </Box>
      )}

      {/* Select-all checkbox header supplement */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, ml: 1 }}>
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleSelectAll}
        />
        <Typography variant="caption" color="text.secondary">Select all</Typography>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#244C77" }} />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <EmptyIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
          <Typography variant="body1" color="text.secondary">No question papers found.</Typography>
        </Box>
      ) : (
        <DynamicTable columns={TABLE_COLUMNS} rows={rows} />
      )}

      {/* Single Delete Confirm */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete Question Paper"
        message={`Are you sure you want to delete "${deleteTarget?.fileName || "this question paper"}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        onConfirm={() => handleDeleteOne(deleteTarget?.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Bulk Delete Confirm */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        title="Delete Selected Papers"
        message={`Are you sure you want to delete ${selectedIds.size} selected question paper(s)? This action cannot be undone.`}
        confirmLabel="Delete All"
        cancelLabel="Cancel"
        severity="danger"
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminQuestionPaper;
